import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import PrintRounded from '@mui/icons-material/PrintRounded';
import { myPay, money, days } from '../api/payroll';
import PayslipView from '../components/payroll/PayslipView';

const DOC_TYPES = {
  OFFER: 'Offer', APPOINTMENT: 'Appointment', HIKE: 'Salary revision', PROMOTION: 'Promotion',
  CONFIRMATION: 'Confirmation', RELIEVING: 'Relieving', EXPERIENCE: 'Experience', OTHER: 'Other',
};

function fmtDate(iso) {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** One payslip in a dialog, printable (the browser's print dialog saves it as a PDF). */
function SlipDialog({ id, onClose, onError }) {
  const [slip, setSlip] = useState(null);
  useEffect(() => {
    let live = true;
    myPay.payslip(id).then((s) => live && setSlip(s)).catch((e) => { onError(e.message); onClose(); });
    return () => { live = false; };
  }, [id, onClose, onError]);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md" scroll="body">
      <DialogContent>
        {!slip ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress /></Box>
        ) : (
          <Box className="print-area">
            <PayslipView slip={slip} company={{ name: slip.companyName, address: slip.companyAddress }} />
          </Box>
        )}
      </DialogContent>
      <DialogActions className="no-print">
        <Button onClick={onClose} color="inherit">Close</Button>
        <Button variant="contained" startIcon={<PrintRounded />} disabled={!slip} onClick={() => window.print()}>
          Print / save as PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Pay & documents: the employee's own released payslips and the letters HR
 * has issued them. Opened from the account menu or a payslip notification.
 */
export default function MyPay({ onError }) {
  const [data, setData] = useState(null);
  const [docs, setDocs] = useState(null);
  const [open, setOpen] = useState(null);
  const closeSlip = useCallback(() => setOpen(null), []);

  useEffect(() => {
    myPay.payslips().then(setData).catch((e) => { onError(e.message); setData({ payslips: [] }); });
    myPay.documents().then(setDocs).catch((e) => { onError(e.message); setDocs([]); });
  }, [onError]);

  async function openDoc(d) {
    // Open the tab now, before the await, so the browser doesn't treat it as a pop-up.
    const w = window.open('', '_blank');
    try {
      const { url } = await myPay.documentUrl(d.id);
      if (w) w.location.href = url; else window.location.href = url;
    } catch (e) {
      if (w) w.close();
      onError(e.message);
    }
  }

  const ytd = data && data.yearToDate;

  return (
    <div>
      <div className="row-between">
        <div>
          <h2>Pay & documents</h2>
          <p className="muted">Your payslips and the letters HR has issued you. Only you and HR can see these.</p>
        </div>
      </div>

      {ytd && ytd.months > 0 && (
        <Stack direction="row" spacing={1.5} sx={{ mb: 2.5, flexWrap: 'wrap', rowGap: 1.5 }}>
          {[['Gross', ytd.gross], ['Income tax', ytd.tds], ['PF', ytd.pf], ['Net received', ytd.net]].map(([k, v]) => (
            <Paper key={k} variant="outlined" sx={{ px: 2, py: 1.25, borderRadius: 3, minWidth: 150 }}>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{k} · {ytd.financialYear}</Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{money(v)}</Typography>
            </Paper>
          ))}
        </Stack>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' }, gap: 2.5, alignItems: 'start' }}>
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Stack direction="row" spacing={1} sx={{ px: 2, py: 1.5, alignItems: 'center', borderBottom: '1px solid #e2e7ee' }}>
            <ReceiptLongRounded fontSize="small" sx={{ color: 'secondary.main' }} />
            <Typography sx={{ fontWeight: 650 }}>Payslips</Typography>
          </Stack>
          {!data ? (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 5 }}><CircularProgress size={26} /></Box>
          ) : data.payslips.length === 0 ? (
            <Typography sx={{ p: 3, fontSize: 13.5, color: 'text.secondary' }}>
              No payslips yet. You'll get a notification when HR releases one.
            </Typography>
          ) : data.payslips.map((p) => (
            <ButtonBase key={p.id} onClick={() => setOpen(p.id)} focusRipple
                        sx={{ display: 'flex', width: '100%', textAlign: 'left', px: 2, py: 1.25, gap: 2,
                              borderBottom: '1px solid #eef1f5', '&:hover': { bgcolor: '#f7f9fb' } }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{p.periodLabel}</Typography>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                  {days(p.paidDays)} paid days{p.lopDays ? ` · ${days(p.lopDays)} LOP` : ''}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{money(p.net)}</Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>net pay</Typography>
              </Box>
            </ButtonBase>
          ))}
        </Paper>

        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Stack direction="row" spacing={1} sx={{ px: 2, py: 1.5, alignItems: 'center', borderBottom: '1px solid #e2e7ee' }}>
            <DescriptionOutlined fontSize="small" sx={{ color: 'secondary.main' }} />
            <Typography sx={{ fontWeight: 650 }}>Letters</Typography>
          </Stack>
          {!docs ? (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 5 }}><CircularProgress size={26} /></Box>
          ) : docs.length === 0 ? (
            <Typography sx={{ p: 3, fontSize: 13.5, color: 'text.secondary' }}>
              No letters yet. Offer, appointment and salary revision letters appear here once HR adds them.
            </Typography>
          ) : docs.map((d) => (
            <ButtonBase key={d.id} onClick={() => openDoc(d)} focusRipple
                        sx={{ display: 'flex', width: '100%', textAlign: 'left', px: 2, py: 1.25, gap: 1.5, alignItems: 'center',
                              borderBottom: '1px solid #eef1f5', '&:hover': { bgcolor: '#f7f9fb' } }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontWeight: 600, fontSize: 14 }}>{d.title}</Typography>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{d.issuedOn ? `Issued ${fmtDate(d.issuedOn)}` : d.fileName}</Typography>
              </Box>
              <Chip size="small" label={DOC_TYPES[d.type] || d.type} sx={{ height: 20, fontSize: 11 }} />
            </ButtonBase>
          ))}
        </Paper>
      </Box>

      {open && <SlipDialog id={open} onClose={closeSlip} onError={onError} />}
    </div>
  );
}
